import { PencilSimpleLine } from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useForm } from "react-hook-form";

import PasswordInput from "common/components/PasswordInput";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import useCustomer from "common/datahooks/useCustomer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import SettingsItemContainer from "../SettingsItemContainer";

export default function Password({
  isEditing,
  startEditing,
  closeEditing,
}: {
  isEditing: boolean;
  startEditing: () => void;
  closeEditing: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const { updatePassword, isUpdatingPassword } = useCustomer();
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<{
    newPassword: string;
    confirmPassword: string;
  }>({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  function onSubmit({
    newPassword,
    confirmPassword,
  }: {
    newPassword: string;
    confirmPassword: string;
  }) {
    if (!newPassword) {
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwords do not match",
      });
      return;
    }

    updatePassword({ newPassword }).then(() => {
      closeEditing();
    });
  }
  function onCancel() {
    closeEditing();
    reset({
      newPassword: "",
      confirmPassword: "",
    });
  }

  return (
    <SettingsItemContainer className={clsx("items-center")}>
      {isEditing ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={clsx(
            "flex w-full items-center gap-2 lg:flex-row",
            isEditing && "flex-col",
          )}
        >
          <div className="flex flex-col lg:flex-row w-full gap-2">
            <FormField
              htmlFor="newPassword"
              label="New password"
              size={isDesktop ? "sm" : "lg"}
              className="flex-1"
              error={errors.newPassword?.message}
            >
              <PasswordInput
                autoFocus
                id="newPassword"
                variant={isDesktop ? "sm" : "lg"}
                placeholder="Enter your new password"
                {...register("newPassword", {
                  required: "New password is required",
                })}
              />
            </FormField>

            <FormField
              htmlFor="confirmPassword"
              label="Confirm password"
              size={isDesktop ? "sm" : "lg"}
              className="flex-1"
              error={errors.confirmPassword?.message}
            >
              <PasswordInput
                id="confirmPassword"
                variant={isDesktop ? "sm" : "lg"}
                placeholder="Confirm your new password"
                {...register("confirmPassword", {
                  required: "New password confirmation is required",
                })}
              />
            </FormField>
          </div>
          <div className="flex gap-x-2 mt-auto ml-auto">
            <Button
              variant="secondary-black"
              size={isDesktop ? "sm" : "md"}
              onClick={onCancel}
              disabled={isUpdatingPassword}
            >
              Cancel
            </Button>
            <Button
              size={isDesktop ? "sm" : "md"}
              type="submit"
              isLoading={isUpdatingPassword}
              variant="secondary-purple"
            >
              Save changes
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="flex flex-col gap-y-0.5">
            <span className="text-sm-bold">Password</span>
            <span className="text-black-500 text-xs-medium">●●●●●●●●●●●</span>
          </div>
          <Button
            variant="ghost-black"
            intent="iconOnly"
            className="ml-auto"
            onClick={startEditing}
          >
            <PencilSimpleLine />
          </Button>
        </>
      )}
    </SettingsItemContainer>
  );
}
